import { Box, LinearProgress, Typography } from "@mui/material";
import { ReactNode } from "react";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";

import styles from "../../../../assets/styles/componentStyles/billingStyles/billingUsage.module.scss";

import { billingSubscriptionFeatures } from "../../../../models/axiosModel";
import { formatDateTime } from "../../../../utils/constants";
import FeatureFreezeWarning from "./featureFreezeWarning";

interface aiCreditsCardProps {
    icon: ReactNode;
    title: string;
    description: string;
    variant?: "complimentary" | "purchased";
    chipText?: string;
    expiresDescription?: string;
    cardData: billingSubscriptionFeatures | null;
}

const AICreditsCard = (props: aiCreditsCardProps) => {

    const { icon, title, description, variant = "complimentary", chipText, expiresDescription, cardData } = props;

    const totalCredits = cardData?.feature_limit_value ? Number(cardData.feature_limit_value) : 0;
    const usedCredits = cardData?.feature_used_amount ? Number(cardData.feature_used_amount) : 0;
    const percentageUsed = cardData?.feature_usage_percent ? Number(cardData.feature_usage_percent) : 0;
    const limitResetDate = cardData?.feature_limit_reset_at ? formatDateTime(cardData.feature_limit_reset_at, "dmTexty") : null;
    const isFreezed = cardData?.flag_freeze;
    const isInfinite = cardData?.flag_infinite;

    return (
        <Box className={`${styles.aiCreditsCard} ${styles[variant]}`}>

            {/* Header */}
            <Box className={styles.headerContainer}>
                <Box className={styles.headerLeft}>
                    <Box className={styles.headerIcon}>{icon}</Box>
                    
                    <Box className={styles.headerContent}>
                        <Typography className={styles.headerTitle}>{title}</Typography>
                        <Typography className={styles.headerDescription}>{description}</Typography>
                    </Box>
                </Box>

                <Box className={styles.headerRight}>
                    {isFreezed && (
                        <FeatureFreezeWarning />
                    )}

                    {chipText && (
                        <Box className={styles.headerChip}>
                            {chipText}
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Usage Block */}
            {isInfinite ? (
                <Box className={styles.headerChip}>
                    Unlimited
                </Box>
            ) : (
                <Box className={styles.usageBlock}>
                    <Box className={styles.usedCreditsContainer}>
                        <Typography>
                            {`${usedCredits} / ${totalCredits}`}
                        </Typography>

                    <Typography>
                        {`${percentageUsed} %`}
                    </Typography>
                </Box>

                    <LinearProgress
                        variant="determinate"
                        value={Math.min(Math.max(percentageUsed, 0), 100)}
                        className={styles.progressBar}
                    />
                </Box>
            )}

            {/* Expires Block */}
            <Box className={styles.expiresContainer}>
                <Box className={styles.expiresIcon}>
                    {limitResetDate
                        ? <CalendarMonthOutlinedIcon />
                        : <AutoAwesomeOutlinedIcon />
                    }
                </Box>

                <Box className={styles.expiresContent}>
                    <Typography className={styles.expiresTitle}>
                        {limitResetDate ? `Reset on ${limitResetDate}` : "Never expires"}
                    </Typography>

                    {expiresDescription && (
                        <Typography className={styles.expiresDescription}>
                            {expiresDescription}
                        </Typography>
                    )}
                </Box>
            </Box>

        </Box>
    )
}

export default AICreditsCard;
